import React, { useState, useEffect } from 'react';
import { Save, RefreshCw, Upload, X } from 'lucide-react';
import { adminAPI, uploadAPI } from '../../services/api';

const SettingsTab = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('general');
  const [uploadingLogo, setUploadingLogo] = useState(null);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const handleResetActivity = async () => {
    const typed = window.prompt(
      'This permanently deletes ALL items, transactions, reports, messages and notifications.\n' +
      'User accounts are KEPT. This cannot be undone.\n\n' +
      'Type RESET to confirm:'
    );
    if (typed !== 'RESET') {
      if (typed !== null) alert('Reset cancelled — you must type RESET exactly.');
      return;
    }
    try {
      setResetting(true);
      const res = await adminAPI.resetActivity();
      const d = res.data?.data || {};
      alert(
        `Done. Cleared:\n` +
        `• ${d.items ?? 0} items\n• ${d.transactions ?? 0} transactions\n` +
        `• ${d.reports ?? 0} reports\n• ${d.messages ?? 0} messages\n` +
        `• ${d.conversations ?? 0} conversations\n• ${d.notifications ?? 0} notifications\n\n` +
        `User accounts were kept.`
      );
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to reset data');
    } finally {
      setResetting(false);
    }
  };

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getSettings();
      if (response.data.success) {
        setSettings(response.data.data);
      }
    } catch (err) {
      console.error('Error loading settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await adminAPI.updateSettings(settings);
      if (response.data.success) {
        alert('Settings saved successfully!');
      }
    } catch (err) {
      alert('Failed to save settings: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings({ ...settings, [key]: value });
  };

  const updateNestedSetting = (parent, key, value) => {
    setSettings({
      ...settings,
      [parent]: {
        ...settings[parent],
        [key]: value,
      },
    });
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <RefreshCw size={40} className="spin" style={{ color: 'var(--primary)' }} />
        <p style={{ marginTop: '16px', color: 'var(--textSecondary)' }}>Loading settings...</p>
      </div>
    );
  }

  const handleLogoUpload = async (e, logoType) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    try {
      setUploadingLogo(logoType);
      const response = await uploadAPI.uploadImage(file);
      const imageUrl = response.data.data.fullSize;

      updateSetting(logoType, imageUrl);
      alert(`${logoType === 'headerLogo' ? 'Header' : 'Favicon'} logo uploaded! Click Save to apply changes.`);
    } catch (err) {
      alert('Failed to upload logo: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploadingLogo(null);
    }
  };

  const handleRemoveLogo = (logoType) => {
    if (window.confirm(`Remove ${logoType === 'headerLogo' ? 'header' : 'favicon'} logo?`)) {
      updateSetting(logoType, null);
    }
  };

  const sections = [
    { id: 'general', name: 'General Settings' },
    { id: 'branding', name: 'Branding' },
    { id: 'notifications', name: 'Notifications' },
    { id: 'payments', name: 'Payments' },
    { id: 'security', name: 'Security' },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ margin: 0 }}>Platform Settings</h1>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 24px',
            background: 'var(--primary)',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '500',
          }}
        >
          <Save size={16} />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <div style={{ display: 'flex', gap: '24px' }}>
        {/* Sidebar */}
        <div style={{ width: '200px', flexShrink: 0 }}>
          <div style={{ background: 'var(--cardBackground)', border: '1px solid var(--border)', borderRadius: '8px', padding: '8px' }}>
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: activeSection === section.id ? 'var(--primary)' : 'transparent',
                  color: activeSection === section.id ? 'white' : 'var(--text)',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  marginBottom: '4px',
                  fontSize: '14px',
                }}
              >
                {section.name}
              </button>
            ))}
          </div>
        </div>

        {/* Settings Content */}
        <div style={{ flex: 1, background: 'var(--cardBackground)', border: '1px solid var(--border)', borderRadius: '8px', padding: '24px' }}>
          {activeSection === 'general' && (
            <div>
              <h2 style={{ marginTop: 0 }}>General Settings</h2>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Site Name</label>
                <input
                  type="text"
                  value={settings?.siteName || ''}
                  onChange={(e) => updateSetting('siteName', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border)',
                    borderRadius: '6px',
                    fontSize: '14px',
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Site Description</label>
                <textarea
                  value={settings?.siteDescription || ''}
                  onChange={(e) => updateSetting('siteDescription', e.target.value)}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border)',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Contact Email</label>
                <input
                  type="email"
                  value={settings?.contactEmail || ''}
                  onChange={(e) => updateSetting('contactEmail', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border)',
                    borderRadius: '6px',
                    fontSize: '14px',
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={settings?.maintenanceMode || false}
                    onChange={(e) => updateSetting('maintenanceMode', e.target.checked)}
                    style={{ width: '20px', height: '20px' }}
                  />
                  <span style={{ fontWeight: '500' }}>Maintenance Mode</span>
                </label>
                <p style={{ margin: '8px 0 0 32px', fontSize: '14px', color: 'var(--textSecondary)' }}>
                  Temporarily disable the site for maintenance
                </p>
              </div>
            </div>
          )}

          {activeSection === 'branding' && (
            <div>
              <h2 style={{ marginTop: 0 }}>Branding</h2>
              <p style={{ color: 'var(--textSecondary)', marginBottom: '24px' }}>
                Customize your platform's visual identity
              </p>

              {/* Header Logo */}
              <div style={{ marginBottom: '32px' }}>
                <label style={{ display: 'block', marginBottom: '12px', fontWeight: '500', fontSize: '16px' }}>
                  Header Logo
                </label>
                <p style={{ fontSize: '14px', color: 'var(--textSecondary)', marginBottom: '12px' }}>
                  This logo will replace the heart icon next to "BabyResell" in the header. Recommended: SVG or PNG, max 56px height.
                </p>

                {settings?.headerLogo ? (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    padding: '16px',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    background: 'var(--background)',
                  }}>
                    <img
                      src={settings.headerLogo}
                      alt="Header Logo"
                      style={{
                        maxHeight: '56px',
                        maxWidth: '200px',
                        objectFit: 'contain',
                      }}
                    />
                    <button
                      onClick={() => handleRemoveLogo('headerLogo')}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 16px',
                        background: 'var(--error)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                      }}
                    >
                      <X size={16} />
                      Remove
                    </button>
                  </div>
                ) : (
                  <div>
                    <input
                      type="file"
                      id="header-logo-upload"
                      accept="image/*"
                      onChange={(e) => handleLogoUpload(e, 'headerLogo')}
                      style={{ display: 'none' }}
                    />
                    <label
                      htmlFor="header-logo-upload"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px 20px',
                        background: uploadingLogo === 'headerLogo' ? 'var(--secondary)' : 'var(--primary)',
                        color: uploadingLogo === 'headerLogo' ? 'var(--text)' : 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: uploadingLogo === 'headerLogo' ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                      }}
                    >
                      <Upload size={16} />
                      {uploadingLogo === 'headerLogo' ? 'Uploading...' : 'Upload Header Logo'}
                    </label>
                  </div>
                )}
              </div>

              {/* Favicon Logo */}
              <div style={{ marginBottom: '32px' }}>
                <label style={{ display: 'block', marginBottom: '12px', fontWeight: '500', fontSize: '16px' }}>
                  Favicon (Browser Tab Icon)
                </label>
                <p style={{ fontSize: '14px', color: 'var(--textSecondary)', marginBottom: '12px' }}>
                  This logo will appear in the browser tab. Recommended: Square image (PNG/ICO), 32x32 or 64x64 pixels.
                </p>

                {settings?.faviconLogo ? (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    padding: '16px',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    background: 'var(--background)',
                  }}>
                    <img
                      src={settings.faviconLogo}
                      alt="Favicon"
                      style={{
                        width: '32px',
                        height: '32px',
                        objectFit: 'contain',
                      }}
                    />
                    <button
                      onClick={() => handleRemoveLogo('faviconLogo')}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 16px',
                        background: 'var(--error)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                      }}
                    >
                      <X size={16} />
                      Remove
                    </button>
                  </div>
                ) : (
                  <div>
                    <input
                      type="file"
                      id="favicon-logo-upload"
                      accept="image/*"
                      onChange={(e) => handleLogoUpload(e, 'faviconLogo')}
                      style={{ display: 'none' }}
                    />
                    <label
                      htmlFor="favicon-logo-upload"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px 20px',
                        background: uploadingLogo === 'faviconLogo' ? 'var(--secondary)' : 'var(--primary)',
                        color: uploadingLogo === 'faviconLogo' ? 'var(--text)' : 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: uploadingLogo === 'faviconLogo' ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                      }}
                    >
                      <Upload size={16} />
                      {uploadingLogo === 'faviconLogo' ? 'Uploading...' : 'Upload Favicon'}
                    </label>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeSection === 'notifications' && (
            <div>
              <h2 style={{ marginTop: 0 }}>Notification Settings</h2>

              {settings?.emailNotifications && Object.entries(settings.emailNotifications).map(([key, value]) => (
                <div key={key} style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={value || false}
                      onChange={(e) => updateNestedSetting('emailNotifications', key, e.target.checked)}
                      style={{ width: '20px', height: '20px' }}
                    />
                    <span style={{ fontWeight: '500', textTransform: 'capitalize' }}>
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                  </label>
                </div>
              ))}
            </div>
          )}

          {activeSection === 'payments' && (
            <div>
              <h2 style={{ marginTop: 0 }}>Payment Settings</h2>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Platform Fee Percentage
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={settings?.platformFeePercent || 5}
                  onChange={(e) => updateSetting('platformFeePercent', parseFloat(e.target.value))}
                  style={{
                    width: '200px',
                    padding: '12px',
                    border: '1px solid var(--border)',
                    borderRadius: '6px',
                    fontSize: '14px',
                  }}
                />
                <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: 'var(--textSecondary)' }}>
                  Percentage of each transaction collected as platform fee (default: 5%)
                </p>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={settings?.stripeEnabled || false}
                    onChange={(e) => updateSetting('stripeEnabled', e.target.checked)}
                    style={{ width: '20px', height: '20px' }}
                  />
                  <span style={{ fontWeight: '500' }}>Enable Stripe Payments</span>
                </label>
              </div>
            </div>
          )}

          {activeSection === 'security' && (
            <div>
              <h2 style={{ marginTop: 0 }}>Security Settings</h2>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Minimum Password Length
                </label>
                <input
                  type="number"
                  min="6"
                  max="32"
                  value={settings?.passwordMinLength || 6}
                  onChange={(e) => updateSetting('passwordMinLength', parseInt(e.target.value))}
                  style={{
                    width: '200px',
                    padding: '12px',
                    border: '1px solid var(--border)',
                    borderRadius: '6px',
                    fontSize: '14px',
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Session Timeout (days)
                </label>
                <input
                  type="number"
                  min="1"
                  max="90"
                  value={settings?.sessionTimeout || 30}
                  onChange={(e) => updateSetting('sessionTimeout', parseInt(e.target.value))}
                  style={{
                    width: '200px',
                    padding: '12px',
                    border: '1px solid var(--border)',
                    borderRadius: '6px',
                    fontSize: '14px',
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={settings?.twoFactorEnabled || false}
                    onChange={(e) => updateSetting('twoFactorEnabled', e.target.checked)}
                    style={{ width: '20px', height: '20px' }}
                  />
                  <span style={{ fontWeight: '500' }}>Enable Two-Factor Authentication</span>
                </label>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={settings?.autoModeration || false}
                    onChange={(e) => updateSetting('autoModeration', e.target.checked)}
                    style={{ width: '20px', height: '20px' }}
                  />
                  <span style={{ fontWeight: '500' }}>Auto-Moderate Content</span>
                </label>
                <p style={{ margin: '8px 0 0 32px', fontSize: '14px', color: 'var(--textSecondary)' }}>
                  Automatically flag inappropriate content
                </p>
              </div>

              {/* Danger Zone */}
              <div style={{
                marginTop: '32px',
                padding: '20px',
                border: '1px solid #e74c3c',
                borderRadius: '10px',
                background: 'rgba(231,76,60,0.06)',
              }}>
                <h3 style={{ marginTop: 0, color: '#e74c3c' }}>⚠️ Danger Zone</h3>
                <p style={{ fontSize: '14px', color: 'var(--textSecondary)', marginBottom: '16px' }}>
                  <strong>Reset test data.</strong> Permanently deletes all listings, transactions,
                  reports, messages, conversations and notifications, and clears saved items &amp;
                  ratings. <strong>User accounts are kept.</strong> This cannot be undone.
                </p>
                <button
                  onClick={handleResetActivity}
                  disabled={resetting}
                  style={{
                    padding: '12px 20px',
                    background: resetting ? 'var(--secondary)' : '#e74c3c',
                    color: resetting ? 'var(--textSecondary)' : 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 600,
                    cursor: resetting ? 'not-allowed' : 'pointer',
                  }}
                >
                  {resetting ? 'Resetting…' : 'Reset test data'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsTab;
